import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Dices } from 'lucide-react'
import styles from './dice-button.module.scss'
import cn from 'classnames'
import usePanelContext from 'refactor/hooks/usePanelContext'
import WithTooltip from 'components/shared/WithTooltip'

interface DiceButtonProps {
    contextStyles?: string
    imageStyles?: string
    onRaffleClick?: () => void
}

const RaffleWheelIcon = ({ size = 16, className }: { size?: number; className?: string }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={className}
    >
        {/* Outer circle */}
        <circle cx="12" cy="12" r="10" />
        {/* Dividing lines to create segments */}
        <line x1="12" y1="2" x2="12" y2="22" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <line x1="5.64" y1="5.64" x2="18.36" y2="18.36" />
        <line x1="5.64" y1="18.36" x2="18.36" y2="5.64" />
    </svg>
)

export default function DiceButton({ contextStyles, imageStyles, onRaffleClick }: DiceButtonProps) {
    const { panelId } = usePanelContext()
    const [showMenu, setShowMenu] = useState(false)
    const buttonRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const [portalTargetNode, setPortalTargetNode] = useState<Element | null>(null)

    const SAFETY_PADDING = 10
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({
        opacity: 0,
        position: 'absolute',
        top: '-9999px',
    })

    const toggleMenu = (e?: React.MouseEvent) => {
        e?.stopPropagation()
        setShowMenu(prev => !prev)
    }

    const handleRaffleClick = () => {
        if (onRaffleClick) {
            onRaffleClick()
        } else {
            alert('Raffle')
        }
        setShowMenu(false)
    }

    // Effect to determine portal target
    useEffect(() => {
        if (showMenu) {
            let targetElement: Element | null = null
            if (panelId) {
                targetElement = document.querySelector(`[data-panel-id="${panelId}"]`)
            }
            setPortalTargetNode(targetElement || document.body)
        } else {
            setPortalTargetNode(null)
        }
    }, [showMenu, panelId])

    // Menu positioning logic
    useEffect(() => {
        if (showMenu && buttonRef.current && menuRef.current && portalTargetNode) {
            const buttonRect = buttonRef.current.getBoundingClientRect()

            if (buttonRect.width === 0 || buttonRect.height === 0) {
                setMenuStyle(prev => ({ ...prev, opacity: 0, top: '-9999px', zIndex: 99999 }))
                return
            }

            const menuWidth = 200
            const menuHeight = 60
            const spaceBetweenButtonAndMenu = 5

            let newStyle: React.CSSProperties = {
                opacity: 1,
                width: `${menuWidth}px`,
                height: `${menuHeight}px`,
                zIndex: 99999,
                overflow: 'hidden',
            }

            if (portalTargetNode === document.body) {
                // Fixed positioning for body portal
                newStyle.position = 'fixed'

                let newLeft = buttonRect.left + buttonRect.width / 2 - menuWidth / 2
                if (newLeft < SAFETY_PADDING) newLeft = SAFETY_PADDING
                if (newLeft + menuWidth > window.innerWidth - SAFETY_PADDING) {
                    newLeft = window.innerWidth - menuWidth - SAFETY_PADDING
                }
                newStyle.left = `${newLeft}px`

                let newTop = buttonRect.top - menuHeight - spaceBetweenButtonAndMenu
                if (newTop < SAFETY_PADDING) {
                    newTop = buttonRect.bottom + spaceBetweenButtonAndMenu
                }
                if (newTop + menuHeight > window.innerHeight - SAFETY_PADDING) {
                    newTop = window.innerHeight - menuHeight - SAFETY_PADDING
                }
                newStyle.top = `${newTop}px`
            } else {
                // Absolute positioning for panel portal
                newStyle.position = 'absolute'
                const panelRect = portalTargetNode.getBoundingClientRect()

                if (panelRect.width === 0 || panelRect.height === 0) {
                    setMenuStyle(prev => ({ ...prev, opacity: 0, top: '-9999px', zIndex: 99999 }))
                    return
                }

                let newLeftAbsolute = (panelRect.width - menuWidth) / 2
                newStyle.left = `${newLeftAbsolute}px`

                const spaceAbove =
                    buttonRect.top - panelRect.top - spaceBetweenButtonAndMenu - SAFETY_PADDING
                const spaceBelow =
                    panelRect.bottom -
                    buttonRect.bottom -
                    spaceBetweenButtonAndMenu -
                    SAFETY_PADDING

                if (spaceAbove >= menuHeight || spaceAbove > spaceBelow) {
                    // Position above button
                    const newBottomAbsolute =
                        panelRect.height -
                        (buttonRect.top - panelRect.top) +
                        spaceBetweenButtonAndMenu
                    newStyle.bottom = `${newBottomAbsolute}px`
                    newStyle.top = 'auto'
                } else {
                    // Position below button
                    const newTopAbsolute =
                        buttonRect.bottom - panelRect.top + spaceBetweenButtonAndMenu
                    newStyle.top = `${newTopAbsolute}px`
                    newStyle.bottom = 'auto'
                }
            }

            setMenuStyle(newStyle)
        } else {
            setMenuStyle(prev => ({ ...prev, opacity: 0, top: '-9999px' }))
        }
    }, [showMenu, portalTargetNode, buttonRef, menuRef, SAFETY_PADDING])

    // Click outside handler
    useEffect(() => {
        if (!showMenu) return
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setShowMenu(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [showMenu])

    const renderMenu = () => {
        if (!showMenu || !portalTargetNode) return null

        return createPortal(
            <div
                ref={menuRef}
                className={styles.diceButtonMenu}
                style={menuStyle}
                onClick={e => e.stopPropagation()}
            >
                <div
                    className={styles.menuItem}
                    onClick={handleRaffleClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') handleRaffleClick()
                    }}
                >
                    <RaffleWheelIcon size={16} className={styles.menuItemIcon} />
                    <span className={styles.menuItemText}>Title Upvalue Raffle</span>
                </div>
            </div>,
            portalTargetNode,
        )
    }

    return (
        <div className={cn(styles.diceButtonContainer, contextStyles)} ref={buttonRef}>
            <WithTooltip tip="Dice Actions">
                <div
                    className={styles.diceButtonMain}
                    onClick={toggleMenu}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') toggleMenu(e as any)
                    }}
                >
                    <div className={styles.diceButtonIcon}>
                        <Dices
                            className={cn(styles.diceIcon, imageStyles)}
                            color="currentColor"
                            size={18}
                        />
                    </div>
                    <div className={styles.diceButtonDropdown}>
                        <i className="fa fa-ellipsis-v" />
                    </div>
                </div>
            </WithTooltip>
            {renderMenu()}
        </div>
    )
}
